if (!customElements.get('ifa-next-edge-carousel')) {
  class IfaNextEdgeCarousel extends HTMLElement {
    connectedCallback() {
      this.sectionRoot = this.closest('.ifa-next-edge') || this;
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.indicators = Array.from(this.querySelectorAll('[data-carousel-indicator]'));
      this.previousButtons = Array.from(this.sectionRoot.querySelectorAll('[data-carousel-previous]'));
      this.nextButtons = Array.from(this.sectionRoot.querySelectorAll('[data-carousel-next]'));
      this.overlayPreviousButtons = Array.from(this.querySelectorAll('[data-carousel-previous]'));
      this.overlayNextButtons = Array.from(this.querySelectorAll('[data-carousel-next]'));
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.compactLayoutQuery = window.matchMedia('(max-width: 1279px)');
      this.autoplayDelay = Number(this.dataset.autoplayDelay) || 5000;
      this.style.setProperty('--ifa-next-edge-carousel-duration', `${this.autoplayDelay}ms`);
      this.autoplayTimer = null;
      this.transitionTimer = null;
      this.transitionDuration = 520;
      this.isTransitioning = false;
      this.hasInitialized = false;
      this.reducedMotion = this.reducedMotionQuery.matches;
      this.isInViewport = false;
      this.isFocusPaused = false;
      this.isSwipePaused = false;
      this.lastInteractionWasPointer = false;
      this.swipeStartX = 0;
      this.swipeStartY = 0;
      this.swipeStartTarget = null;
      this.index = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.slidesContainer = this.querySelector('[data-carousel-slides]');
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);

      if (this.slides.length < 2) return;
      if (this.index < 0) this.index = 0;

      const { signal } = this.abortController;
      this.previousButtons.forEach((button) => {
        button.addEventListener('click', () => this.showSlide(this.index - 1, { wrap: false }), { signal });
      });
      this.nextButtons.forEach((button) => {
        button.addEventListener('click', () => this.showSlide(this.index + 1, { wrap: false }), { signal });
      });
      this.sectionRoot.addEventListener('pointerdown', this.onPointerInteraction, { capture: true, signal });
      window.addEventListener('keydown', this.onKeyboardInteraction, { signal });
      this.sectionRoot.addEventListener('focusin', (event) => {
        if (!this.lastInteractionWasPointer && this.sectionRoot.contains(event.target)) {
          this.setPauseState('focus', true);
        }
      }, { signal });
      this.sectionRoot.addEventListener('focusout', (event) => {
        if (!this.lastInteractionWasPointer && !this.sectionRoot.contains(event.relatedTarget)) {
          this.setPauseState('focus', false);
        }
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updateAutoplay(), { signal });
      this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange, { signal });
      this.compactLayoutQuery.addEventListener('change', () => this.updateControlState(), { signal });
      this.bindTouchSwipe(signal);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isInViewport = entries.some((entry) => entry.isIntersecting);
        this.updateAutoplay();
      }, { threshold: 0.25 });
      this.intersectionObserver.observe(this);

      this.preloadObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.preloadSlideImages();
        this.preloadObserver?.disconnect();
      }, { rootMargin: '600px 0px' });
      this.preloadObserver.observe(this);
      this.showSlide(this.index, { instant: true });
      this.hasInitialized = true;
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      this.preloadObserver?.disconnect();
      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.transitionTimer);
    }

    bindTouchSwipe(signal) {
      this.slidesContainer?.addEventListener('touchstart', this.onSwipeTouchStart, { passive: true, signal });
      this.slidesContainer?.addEventListener('touchend', this.onSwipeTouchEnd, { passive: true, signal });
      this.slidesContainer?.addEventListener('touchcancel', this.onSwipeTouchCancel, { passive: true, signal });
    }

    onPointerInteraction = () => {
      this.lastInteractionWasPointer = true;
      this.setPauseState('focus', false);
    };

    onKeyboardInteraction = () => {
      this.lastInteractionWasPointer = false;
    };

    isSwipeInteractiveTarget(target) {
      return Boolean(target?.closest?.('button, a, [data-carousel-previous], [data-carousel-next]'));
    }

    onSwipeTouchStart = (event) => {
      if (!this.compactLayoutQuery.matches || event.touches.length !== 1) return;
      if (this.isSwipeInteractiveTarget(event.target)) return;

      const [touch] = event.touches;
      this.swipeStartX = touch.clientX;
      this.swipeStartY = touch.clientY;
      this.swipeStartTarget = event.target;
      this.setPauseState('swipe', true);
    };

    onSwipeTouchEnd = (event) => {
      if (!this.swipeStartTarget || event.changedTouches.length !== 1) return;

      const [touch] = event.changedTouches;
      const deltaX = touch.clientX - this.swipeStartX;
      const deltaY = touch.clientY - this.swipeStartY;
      this.resetSwipeState();
      this.setPauseState('swipe', false);

      if (Math.abs(deltaX) < 40 || Math.abs(deltaY) >= Math.abs(deltaX)) return;

      if (deltaX < 0) {
        this.showSlide(this.index + 1, { wrap: false });
        return;
      }

      this.showSlide(this.index - 1, { wrap: false });
    };

    onSwipeTouchCancel = () => {
      if (!this.swipeStartTarget) return;
      this.resetSwipeState();
      this.setPauseState('swipe', false);
    };

    resetSwipeState() {
      this.swipeStartTarget = null;
    }

    preloadSlideImages() {
      this.slidesContainer?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
        image.setAttribute('fetchpriority', 'low');
      });
    }

    onReducedMotionChange(event) {
      this.reducedMotion = event.matches;

      if (this.reducedMotion) {
        this.syncSlideState(this.index);
        return;
      }

      this.updateAutoplay();
    }

    showSlide(nextIndex, { wrap = true, instant = false } = {}) {
      const index = wrap
        ? (nextIndex + this.slides.length) % this.slides.length
        : Math.max(0, Math.min(nextIndex, this.slides.length - 1));

      const previousIndex = this.index;
      const shouldAnimate = this.hasInitialized
        && !instant
        && !this.reducedMotion
        && !this.isTransitioning
        && index !== previousIndex;

      if (!shouldAnimate) {
        if (!this.isTransitioning) this.syncSlideState(index);
        return;
      }

      const isWrappingForward = wrap && previousIndex === this.slides.length - 1 && index === 0;
      const isWrappingBackward = wrap && previousIndex === 0 && index === this.slides.length - 1;
      const direction = isWrappingForward || (!isWrappingBackward && index > previousIndex) ? 1 : -1;
      const outgoingSlide = this.slides[previousIndex];
      const incomingSlide = this.slides[index];

      this.index = index;
      this.isTransitioning = true;
      window.clearTimeout(this.transitionTimer);

      this.slides.forEach((slide, slideIndex) => {
        slide.classList.remove('is-active', 'is-leaving');
        slide.style.removeProperty('--ifa-next-edge-slide-offset');
        slide.style.removeProperty('--ifa-next-edge-slide-leave-offset');

        if (slideIndex !== previousIndex && slideIndex !== index) {
          slide.hidden = true;
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      outgoingSlide.hidden = false;
      outgoingSlide.setAttribute('aria-hidden', 'true');
      outgoingSlide.classList.add('is-active');
      outgoingSlide.style.setProperty('--ifa-next-edge-slide-leave-offset', `${direction * -24}px`);

      incomingSlide.hidden = false;
      incomingSlide.setAttribute('aria-hidden', 'false');
      incomingSlide.style.setProperty('--ifa-next-edge-slide-offset', `${direction * 24}px`);

      void incomingSlide.offsetWidth;

      outgoingSlide.classList.remove('is-active');
      outgoingSlide.classList.add('is-leaving');
      incomingSlide.classList.add('is-active');

      this.syncIndicators();
      this.updateControlState();
      this.updateAutoplay();

      this.transitionTimer = window.setTimeout(() => {
        outgoingSlide.hidden = true;
        outgoingSlide.classList.remove('is-leaving');
        outgoingSlide.style.removeProperty('--ifa-next-edge-slide-leave-offset');
        incomingSlide.style.removeProperty('--ifa-next-edge-slide-offset');
        this.isTransitioning = false;
        this.updateAutoplay();
      }, this.transitionDuration);
    }

    syncSlideState(index) {
      window.clearTimeout(this.transitionTimer);
      this.isTransitioning = false;
      this.index = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.remove('is-leaving');
        slide.classList.toggle('is-active', isActive);
        slide.style.removeProperty('--ifa-next-edge-slide-offset');
        slide.style.removeProperty('--ifa-next-edge-slide-leave-offset');
        slide.toggleAttribute('hidden', !isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.syncIndicators();
      this.updateControlState();
      this.updateAutoplay();
    }

    syncIndicators() {
      this.indicators.forEach((indicator, indicatorIndex) => {
        indicator.classList.toggle('is-active', indicatorIndex === this.index);
      });
    }

    updateControlState() {
      const useBoundedControls = this.compactLayoutQuery.matches;
      const isFirstSlide = this.index === 0;
      const isLastSlide = this.index === this.slides.length - 1;

      this.classList.toggle('is-first-slide', isFirstSlide);
      this.classList.toggle('is-last-slide', isLastSlide);

      this.previousButtons.forEach((button) => {
        button.disabled = useBoundedControls && isFirstSlide;
      });
      this.nextButtons.forEach((button) => {
        button.disabled = useBoundedControls && isLastSlide;
      });
      this.overlayPreviousButtons.forEach((button) => {
        button.hidden = isFirstSlide;
      });
      this.overlayNextButtons.forEach((button) => {
        button.hidden = isLastSlide;
      });
    }

    updateAutoplay() {
      window.clearTimeout(this.autoplayTimer);

      const canAutoplay = this.isInViewport
        && !this.reducedMotion
        && !document.hidden
        && !this.isFocusPaused
        && !this.isSwipePaused
        && !this.isTransitioning;

      this.classList.toggle('is-autoplaying', canAutoplay);
      this.slidesContainer?.setAttribute('aria-live', canAutoplay ? 'off' : 'polite');
      if (!canAutoplay) return;

      this.restartProgress();
      this.autoplayTimer = window.setTimeout(() => {
        this.showSlide(this.index + 1);
      }, this.autoplayDelay);
    }

    setPauseState(type, paused) {
      if (type === 'focus') {
        if (this.isFocusPaused === paused) return;
        this.isFocusPaused = paused;
      } else if (type === 'swipe') {
        if (this.isSwipePaused === paused) return;
        this.isSwipePaused = paused;
      } else {
        return;
      }

      this.updateAutoplay();
    }

    restartProgress() {
      const activeIndicator = this.indicators[this.index];
      if (!activeIndicator) return;

      activeIndicator.classList.remove('is-progressing');
      void activeIndicator.offsetWidth;
      activeIndicator.classList.add('is-progressing');
    }
  }

  customElements.define('ifa-next-edge-carousel', IfaNextEdgeCarousel);
}
